import React from 'react';

/**
 * Formats a phone number with Brazilian phone mask
 * @param value - The phone number string to format
 * @returns Formatted phone number string
 */
export const formatPhoneNumber = (value: string): string => {
  // Remove all non-digit characters
  const cleaned = value.replace(/\D/g, '');
  
  // Apply mask based on length
  if (cleaned.length <= 2) {
    return cleaned;
  } else if (cleaned.length <= 7) {
    return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2)}`;
  } else if (cleaned.length <= 11) {
    return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7)}`;
  } else {
    // Limit to 11 digits (Brazilian phone number max)
    const limited = cleaned.slice(0, 11);
    return `(${limited.slice(0, 2)}) ${limited.slice(2, 7)}-${limited.slice(7)}`;
  }
};

/**
 * Removes phone mask formatting to get only digits
 * @param value - The formatted phone number string
 * @returns Phone number with only digits
 */
export const cleanPhoneNumber = (value: string): string => {
  return value.replace(/\D/g, '');
};

/**
 * Validates if a phone number has the correct format and length
 * @param value - The phone number string to validate
 * @returns boolean indicating if the phone number is valid
 */
export const isValidPhoneNumber = (value: string): boolean => {
  const cleaned = cleanPhoneNumber(value);
  // Brazilian phone numbers should have 10 or 11 digits
  // 10 digits: landline (XX) XXXX-XXXX
  // 11 digits: mobile (XX) 9XXXX-XXXX
  return cleaned.length === 10 || cleaned.length === 11;
};

/**
 * Custom hook for phone number input handling
 * @param initialValue - Initial phone number value
 * @returns Object with value, onChange handler, and validation
 */
export const usePhoneMask = (initialValue: string = '') => {
  const [value, setValue] = React.useState(() => formatPhoneNumber(initialValue));
  
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(event.target.value);
    setValue(formatted);
  };
  
  const isValid = isValidPhoneNumber(value);
  const cleanValue = cleanPhoneNumber(value);
  
  return {
    value,
    onChange: handleChange,
    isValid,
    cleanValue,
    setValue: (newValue: string) => setValue(formatPhoneNumber(newValue))
  };
};

// For use without React hook
export const phoneMaskProps = {
  maxLength: 15, // (XX) XXXXX-XXXX format
  placeholder: '(11) 99999-9999',
  inputProps: {
    maxLength: 15,
  }
};
